import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useUrlState } from '../hooks/useUrlState';
import { apiClient } from '../lib/api-client';
import { formatDateTime } from '../utils/datetime';

type Ticket = {
  id: string;
  code: string;
  planId: string;
  plan: {
    id: string;
    tier: string;
    name: string;
    displayName: Record<string, string>;
  };
  issuer: {
    id: string;
    did: string;
    handle: string;
    displayName: string | null;
  };
  issuedAt: string;
  redemptionDeadline: string | null;
  validDays: number;
  planExpiresAt: string | null;
  maxRedemptions: number | null;
  redemptionCount: number;
  status: string;
  memo: string | null;
};

const statusStyles: Record<string, string> = {
  UNUSED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  ACTIVE: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  EXPIRED: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  REVOKED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
};

// URL に不正値が入った場合のフォールバック用の許可値。
const TICKET_FILTERS = ['all', 'unused', 'active', 'expired', 'revoked'] as const;

function TicketsPage() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // フィルタは URL クエリで管理し、戻る / 進む と URL 共有に対応する
  const [{ filter }, setUrlState] = useUrlState({ filter: 'all' }, { filter: TICKET_FILTERS });

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getTickets();
      setTickets(data.tickets);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const now = new Date();
  const filteredTickets = tickets
    .filter(ticket => {
      if (filter === 'all') return true;
      return ticket.status === filter.toUpperCase();
    })
    .map(ticket => {
      // ticket.redemptionDeadline が過去の場合、 EXPIRED として扱う
      if (ticket.redemptionDeadline) {
        const redemptionDeadline = new Date(ticket.redemptionDeadline);
        if (redemptionDeadline < now) ticket.status = 'EXPIRED';
      }
      return ticket;
    });

  if (loading) {
    return (
      <div className='flex min-h-[400px] items-center justify-center'>
        <div className='text-lg text-gray-600 dark:text-gray-300'>Loading tickets...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20'>
        <p className='text-red-800 dark:text-red-200'>Error: {error}</p>
        <button
          onClick={loadTickets}
          className='mt-2 rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600'
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
        <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>Plan Tickets</h1>
        <Link
          to='/tickets/issue'
          className='rounded-md bg-blue-600 px-4 py-2 text-center text-white transition-colors hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600'
        >
          Issue New Ticket
        </Link>
      </div>

      {/* Filters */}
      <div className='flex flex-wrap gap-2'>
        <button
          onClick={() => setUrlState({ filter: 'all' })}
          className={`rounded-md px-4 py-2 ${
            filter === 'all'
              ? 'bg-gray-900 text-white dark:bg-gray-600'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          All ({tickets.length})
        </button>
        <button
          onClick={() => setUrlState({ filter: 'unused' })}
          className={`rounded-md px-4 py-2 ${
            filter === 'unused'
              ? 'bg-gray-900 text-white dark:bg-gray-600'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          Unused ({tickets.filter(t => t.status === 'UNUSED').length})
        </button>
        <button
          onClick={() => setUrlState({ filter: 'active' })}
          className={`rounded-md px-4 py-2 ${
            filter === 'active'
              ? 'bg-gray-900 text-white dark:bg-gray-600'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          Active ({tickets.filter(t => t.status === 'ACTIVE').length})
        </button>
        <button
          onClick={() => setUrlState({ filter: 'expired' })}
          className={`rounded-md px-4 py-2 ${
            filter === 'expired'
              ? 'bg-gray-900 text-white dark:bg-gray-600'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          Expired ({tickets.filter(t => t.status === 'EXPIRED').length})
        </button>
        <button
          onClick={() => setUrlState({ filter: 'revoked' })}
          className={`rounded-md px-4 py-2 ${
            filter === 'revoked'
              ? 'bg-gray-900 text-white dark:bg-gray-600'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          Revoked ({tickets.filter(t => t.status === 'REVOKED').length})
        </button>
      </div>

      {/* Tickets Table - Desktop Only */}
      <div className='hidden rounded-lg bg-white shadow-md md:block dark:bg-gray-800'>
        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
            <thead className='bg-gray-50 dark:bg-gray-700'>
              <tr>
                <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                  Code
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                  Plan
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                  Issuer
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                  Status
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                  Usage
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                  Valid Period
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                  Issued At
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                  Redemption Deadline
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800'>
              {filteredTickets.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className='px-6 py-4 text-center text-gray-500 dark:text-gray-400'
                  >
                    No tickets found
                  </td>
                </tr>
              ) : (
                filteredTickets.map(ticket => (
                  <tr key={ticket.id} className='hover:bg-gray-50 dark:hover:bg-gray-700/50'>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <Link
                        to={`/tickets/${ticket.id}`}
                        className='font-mono font-semibold text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300'
                      >
                        {ticket.code}
                      </Link>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <div className='text-sm font-medium text-gray-900 dark:text-white'>
                        {ticket.plan.displayName?.en || ticket.plan.name}
                      </div>
                      <div className='text-sm text-gray-500 dark:text-gray-400'>
                        {ticket.plan.tier}
                      </div>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <div className='text-sm text-gray-900 dark:text-white'>
                        {ticket.issuer.displayName || ticket.issuer.handle}
                      </div>
                      <div className='text-sm text-gray-500 dark:text-gray-400'>
                        @{ticket.issuer.handle}
                      </div>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <span
                        className={`inline-flex rounded-full px-2 text-xs leading-5 font-semibold ${
                          statusStyles[ticket.status] ||
                          'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {ticket.status}
                      </span>
                    </td>
                    <td className='px-6 py-4 text-sm whitespace-nowrap text-gray-900 dark:text-white'>
                      {ticket.redemptionCount} / {ticket.maxRedemptions ?? '∞'}
                    </td>
                    <td className='px-6 py-4 text-sm whitespace-nowrap text-gray-900 dark:text-white'>
                      {ticket.planExpiresAt ? (
                        <div>
                          <div>
                            {formatDateTime(ticket.planExpiresAt, {
                              dateStyle: 'medium',
                              timeStyle: 'short'
                            })}
                          </div>
                          <div className='text-xs text-gray-500 dark:text-gray-400'>Absolute</div>
                        </div>
                      ) : (
                        `${ticket.validDays} days`
                      )}
                    </td>
                    <td className='px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400'>
                      {formatDateTime(ticket.issuedAt, {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      }) || 'N/A'}
                    </td>
                    <td className='px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400'>
                      {formatDateTime(ticket.redemptionDeadline, {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      }) || 'N/A'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tickets Cards - Mobile Only */}
      <div className='space-y-4 md:hidden'>
        {filteredTickets.length === 0 ? (
          <div className='rounded-lg bg-white p-6 text-center text-gray-500 shadow-md dark:bg-gray-800 dark:text-gray-400'>
            No tickets found
          </div>
        ) : (
          filteredTickets.map(ticket => (
            <Link
              key={ticket.id}
              to={`/tickets/${ticket.id}`}
              className='block rounded-lg bg-white p-4 shadow-md transition-shadow hover:shadow-lg dark:bg-gray-800'
            >
              {/* Code and Status */}
              <div className='mb-3 flex items-start justify-between'>
                <div className='min-w-0 flex-1'>
                  <div className='truncate font-mono text-base font-semibold text-blue-600 dark:text-blue-400'>
                    {ticket.code}
                  </div>
                  <div className='mt-1 truncate text-sm text-gray-500 dark:text-gray-400'>
                    {ticket.plan.displayName?.en || ticket.plan.name}
                  </div>
                </div>
                <span
                  className={`flex-shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    statusStyles[ticket.status] ||
                    'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  {ticket.status}
                </span>
              </div>

              {/* Details */}
              <div className='space-y-1 border-t border-gray-200 pt-3 text-sm dark:border-gray-700'>
                <div className='flex justify-between gap-2'>
                  <span className='flex-shrink-0 text-gray-500 dark:text-gray-400'>Issuer:</span>
                  <span className='truncate text-right text-gray-900 dark:text-white'>
                    @{ticket.issuer.handle}
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-gray-500 dark:text-gray-400'>Usage:</span>
                  <span className='text-gray-900 dark:text-white'>
                    {ticket.redemptionCount} / {ticket.maxRedemptions ?? '∞'}
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-gray-500 dark:text-gray-400'>Valid:</span>
                  <span className='text-gray-900 dark:text-white'>
                    {ticket.planExpiresAt
                      ? formatDateTime(ticket.planExpiresAt, {
                          dateStyle: 'short',
                          timeStyle: 'short'
                        })
                      : `${ticket.validDays} days`}
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-gray-500 dark:text-gray-400'>Issued:</span>
                  <span className='text-gray-900 dark:text-white'>
                    {formatDateTime(ticket.issuedAt, { dateStyle: 'short' })}
                  </span>
                </div>
                {ticket.redemptionDeadline && (
                  <div className='flex justify-between'>
                    <span className='text-gray-500 dark:text-gray-400'>Deadline:</span>
                    <span className='text-gray-900 dark:text-white'>
                      {formatDateTime(ticket.redemptionDeadline, { dateStyle: 'short' })}
                    </span>
                  </div>
                )}
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}

export default TicketsPage;
